import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import io from 'socket.io-client';
import Message from './Message';
import MessageInput from './MessageInput';
import ContactForm from './ContactForm';

const BACKEND_URL = process.env.NODE_ENV === 'production' 
  ? 'https://chatbot-backend-21oh.onrender.com'
  : 'http://localhost:5000';

// Add n8n webhook URL
const N8N_WEBHOOK_URL = 'https://integration.insomniacs.in/webhook/24da65c7-460f-45c0-85a9-e11d2156dd87/chat';

const socket = io(BACKEND_URL);

const INITIAL_MESSAGE = {
  user: 'Bot',
  message: "👋 Hey there! I'm the ACX Assistant - your digital buddy at Absolute CX. I help real estate pros like you explore how tech can 10x your performance. Let's get started! First, tell me a bit about you 👇 \nAre you a :",
  isBot: true,
  timestamp: new Date(),
  options: ['🏢 Real Estate Developer', '🤝 Real Estate Channel Partner', '🌐 Other']
};

const PRESALES_OPTIONS = {
  message: "Meet ACX Convo AI – your tireless digital pre-sales assistant.\nIt never sleeps, follows up instantly, and tracks performance like a pro. Want to explore how it boosts your leads 10x?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const SALES_OPTIONS = {
  message: "Our Sales Management Platform simplifies bookings, tracks TATs, and gives you real-time deal data 📊\nReady to sell smarter, not harder?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const POSTSALES_OPTIONS = {
  message: "After-sales experience makes or breaks brand love ❤️\nOur Customer Loyalty Platform ensures smoother handovers, payments, and updates.\nWant to see how?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const REFERRAL_OPTIONS = {
  message: "Empower happy customers to become your sales team! \nOur Referral Engine rewards them, tracks referrals, and boosts revenue. Shall we dive in?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const CHANNEL_PARTNER_OPTIONS = {
  message: "Say goodbye to spreadsheets 📉\nOur CP Management App tracks performance, rewards loyalty, and simplifies bookings. \nWant to take a look?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const MARKETING_OPTIONS = {
  message: "Stop guessing. Start marketing with precision 🎯\nOur Marketing Cloud uses AI, automation, and CDP data to fuel conversions.\nWant to campaign smarter?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const POST_POSSESSION_OPTIONS = {
  message: "Property handover isn't the end – it's the beginning!\nOur Facility Management Platform streamlines amenities blocking, visitor management & more.\nWant a better resident experience?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const PHYSICAL_SITE_VISITS_OPTIONS = {
  message: "Ditch the paper diaries 🗓️\nOur Site Visit Scheduler tracks visits, automates follow-ups, and boosts walk-in conversions.\nWant a walkthrough?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const VIRTUAL_SITE_VISITS_OPTIONS = {
  message: "Sell the view before they arrive 🌇\nOur VR & 3D Tour Solutions give customers an immersive walkthrough, from anywhere.\nCurious how it works?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const COMMERCIAL_PROPERTY_MANAGEMENT_OPTIONS = {
  message: "One dashboard. All your commercial assets 📈\nLease tracking, tenant records, AMC, documents – all digitized.\nShall we explore it?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const CRM_SOFTWARE_OPTIONS = {
  message: "Meet Totality CRM – built for real estate, by real estate experts 🧠\nIt connects marketing, sales, and service into a single view.\nReady to see it in action?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const SALESFORCE_OPTIONS = {
  message: "We're India's first Salesforce partners focused only on real estate 🛠️\nFaster TAT, better ROI, custom layers.\nWant to see how we transform Salesforce for developers?",
  options: [
    '✅ Yes, show me more',
    '⏭️ Not now'
  ]
};

const WORKFLOW_MESSAGES = {
  'Real Estate Developer': {
    message: "Awesome! We specialize in end-to-end digital transformation for real estate developers.\nWhich department would you like to supercharge with tech?\n👉 Select one to explore:",
    options: [
      '🔍 Pre-Sales – AI Bots & Lead Qualification',
      '🏷️ Sales – Closing Deals Faster',
      '🤝 Post Sales – Delightful Handovers & Service',
      '🔁 Referral Sales – Customer Driven Growth',
      '📲 Channel Partner Sales – Manage CPs like a boss',
      '📢 Marketing – Smarter, Faster campaigns',
      '🛠️ Post Possession – Facility & Grievance Management',
      '🧭 Physical Site Visits – Schedule & Track Site Visits',
      '🖥️ Virtual Site Visits – Immersive 3D & VR Tours',
      '🏢 Commercial Property Mgmt – Manage Assets, Tenants, Leases',
      '🧠 CRM Software (Totality) – 360° view of every customer',
      '⚙️ Salesforce – Custom Salesforce Implementations'
    ]
  },
  'Real Estate Channel Partner': {
    message: "We love curious explorers 🤓 Whether you're a Channel Partner, investor, or tech geek, there's something for everyone here. \nCan you share what you're looking for?",
    showForm: true,
    formFields: ['name', 'phone', 'email', 'organization', 'department', 'city', 'comments']
  },
  'Other': {
    message: "We love curious explorers 🤓 Whether you're a Channel Partner, investor, or tech geek, there's something for everyone here. \nCan you share what you're looking for?",
    showForm: true,
    formFields: ['name', 'phone', 'email', 'organization', 'department', 'city', 'comments']
  }
};

// Add a function to clean option text for matching
const cleanOptionText = (option) => {
  return option
    .replace('🔍 ', '')
    .replace('🏷️ ', '')
    .replace('🤝 ', '')
    .replace('🔁 ', '')
    .replace('📲 ', '')
    .replace('📢 ', '')
    .replace('🛠️ ', '')
    .replace('🧭 ', '')
    .replace('🖥️ ', '')
    .replace('🏢 ', '')
    .replace('🧠 ', '')
    .replace('⚙️ ', '')
    .replace(' – AI Bots & Lead Qualification', '')
    .replace(' – Closing Deals Faster', '')
    .replace(' – Delightful Handovers & Service', '')
    .replace(' – Customer Driven Growth', '')
    .replace(' – Manage CPs like a boss', '')
    .replace(' – Smarter, Faster campaigns', '')
    .replace(' – Facility & Grievance Management', '')
    .replace(' – Schedule & Track Site Visits', '')
    .replace(' – Immersive 3D & VR Tours', '')
    .replace(' – Manage Assets, Tenants, Leases', '')
    .replace(' – 360° view of every customer', '')
    .replace(' – Custom Salesforce Implementations', '');
};

// eslint-disable-next-line no-unused-vars
const getCurrentOptionType = (messages) => {
  const lastBotMessage = [...messages].reverse().find(msg => msg.isBot && msg.options?.length === 2);
  if (!lastBotMessage) return null;

  // Get the context from previous user message
  const userMessages = messages.filter(msg => !msg.isBot);
  const lastUserMessage = userMessages[userMessages.length - 2]; // Get the second-to-last user message
  if (!lastUserMessage) return null;

  const cleanedOption = cleanOptionText(lastUserMessage.message);

  return cleanedOption;
};

// Update SALES_URLs keys to match cleaned option text
const SALES_URLS = {
  'Pre-Sales': 'https://acxconvoai.com/',
  'Sales': 'https://absolutecx.com/solutions/sales-management/',
  'Post Sales': 'https://absolutecx.com/solutions/customer-loyalty-management/',
  'Referral Sales': 'https://absolutecx.com/solutions/customer-loyalty-management/',
  'Channel Partner Sales': 'https://absolutecx.com/solutions/channel-partner-management/',
  'Marketing': 'https://absolutecx.com/solutions/marketing-automation-and-campaign-management/',
  'Post Possession': 'https://absolutecx.com/solutions/facility-management/',
  'Physical Site Visits': 'https://absolutecx.com/solutions/physical-site-visit-management/',
  'Virtual Site Visits': 'https://absolutecx.com/solutions/virtual-site-visit-management/',
  'Commercial Property Mgmt': 'https://absolutecx.com/solutions/commercial-property-management/',
  'CRM Software': 'https://absolutecx.com/solutions/crm-software-management/',
  'Salesforce': 'https://absolutecx.com/'
};

const Chat = ({ externalState }) => {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isTyping, setIsTyping] = useState(false);
  const [userType, setUserType] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [formMessage, setFormMessage] = useState("Share your details below 👇");
  const [lastSelectedOption, setLastSelectedOption] = useState(null);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isChatOpen, setIsChatOpen] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [currentOption, setCurrentOption] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isRedirected, setIsRedirected] = useState(false); // Track if we're returning from a redirect
  // Add a ref to track if we're in the process of resetting
  const isResettingRef = useRef(false);

  // Sync with external state if provided
  useEffect(() => {
    if (externalState && typeof externalState.isOpen !== 'undefined') {
      setIsChatOpen(externalState.isOpen);
    }
  }, [externalState]);

  // Function to save chat history to localStorage
  const saveChatHistory = (msgs, userTypeVal, lastOptionVal) => {
    try {
      // Create a serializable version of messages (handle Date objects)
      const serializableMessages = msgs.map(msg => ({
        ...msg,
        timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp
      }));
      
      const historyData = {
        messages: serializableMessages,
        userType: userTypeVal,
        lastSelectedOption: lastOptionVal,
        sessionId: sessionIdRef.current
      };
      
      localStorage.setItem('acx_chat_history', JSON.stringify(historyData));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  };

  // Function to load chat history from localStorage
  const loadChatHistory = () => {
    try {
      const historyData = localStorage.getItem('acx_chat_history');
      if (historyData) {
        const parsed = JSON.parse(historyData);

        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsed.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        
        setMessages(messagesWithDates);
        setUserType(parsed.userType);
        setLastSelectedOption(parsed.lastSelectedOption);
        if (parsed.sessionId) sessionIdRef.current = parsed.sessionId;
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading chat history:', error);
      return false;
    }
  };

  // Generate session ID on component mount
  useEffect(() => {
    const newSessionId = 'session_' + Math.random().toString(36).substr(2, 9);
    sessionIdRef.current = newSessionId;
    
    // Check if we have chat history in localStorage
    const hasHistory = localStorage.getItem('acx_chat_history') !== null;
    if (hasHistory) {
      setIsRedirected(true);
      setIsChatOpen(true); // Make sure chat is open when returning
      
      // Notify parent component to open chat window
      if (window.parent) {
        window.parent.postMessage({ type: 'OPEN_CHAT' }, '*');
      }
    }
    
    // Connect socket when component mounts
    if (socket.disconnected) {
      socket.connect();
    }
    
    // Cleanup function
    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, []);

  // Add function to handle URL parameters and localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const option = urlParams.get('option');
    
    // First check for chat history in localStorage
    const hasHistory = loadChatHistory();
    
    if (hasHistory) {
      // Add a continuation message and ensure the chat is visible
      setTimeout(() => {
        // Always show the form after redirection for a better user experience
        setFormMessage("Want us to reach out with a quick walkthrough or demo about " + 
          (lastSelectedOption || "our solutions") + "? Share your details below 👇");
        setShowForm(true);
        setFormFields(['name', 'email', 'phone', 'organization', 'department', 'city', 'comments']);
        
        const continuationMessage = {
          user: 'Bot',
          message: `Welcome back! Would you like to continue exploring our solutions?`,
          isBot: true,
          timestamp: new Date(),
          options: WORKFLOW_MESSAGES['Real Estate Developer'].options
        };
        setMessages(prev => [...prev, continuationMessage]);
        
        // Ensure the parent bubble shows the chat
        if (window.parent) {
          window.parent.postMessage({ type: 'OPEN_CHAT' }, '*');
        }
      }, 1000);
      
      // Clear the history from localStorage after loading
      localStorage.removeItem('acx_chat_history');
    } else if (option) {
      setCurrentOption(option);
      setIsChatOpen(true); // Make sure chat is open when an option is provided
      const botResponse = {
        user: 'Bot',
        message: `Welcome back! Please share your contact details so our team can provide you with a personalized demo of our ${option} solution.`,
        isBot: true,
        timestamp: new Date()
      };
      setMessages([botResponse]);
      setShowForm(true);
      setFormFields(['name', 'email', 'phone', 'organization', 'department', 'city', 'comments']);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleSocketError = (error) => {
      console.error('Socket connection error:', error);
    };

    const handleConnect = () => {
      console.log('Socket connected successfully');
    };

    const handleDisconnect = (reason) => {
      console.log('Socket disconnected:', reason);
    };

    socket.on('connect_error', handleSocketError);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('message', (message) => {
      setMessages((prevMessages) => [...prevMessages, message]);
      setIsTyping(false);
    });

    socket.on('typing', () => {
      setIsTyping(true);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 2000);
    });

    return () => {
      socket.off('connect_error', handleSocketError);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('message');
      socket.off('typing');
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOptionSelect = (option) => {
    const userMessage = {
      user: 'User',
      message: option,
      isBot: false,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    if (!userType) {
      setUserType(option);
      const workflow = WORKFLOW_MESSAGES[option.replace('🏢 ', '').replace('🤝 ', '').replace('🌐 ', '')];
      
      if (workflow?.showForm) {
        setShowForm(true);
        setFormFields(workflow.formFields);
        setFormMessage("Share your details below 👇");
      }

      const botResponse = {
        user: 'Bot',
        message: workflow.message,
        isBot: true,
        timestamp: new Date(),
        options: workflow.options
      };
      
      setTimeout(() => {
        setMessages(prev => [...prev, botResponse]);
      }, 500);
    } else {
      const cleanedOption = option.trim();
      
      if (cleanedOption === '✅ Yes, show me more' && lastSelectedOption) {
        // Use the lastSelectedOption to find the correct URL
        const url = SALES_URLS[lastSelectedOption];
        if (url) {
          const botResponse = {
            user: 'Bot',
            message: `Great choice! While you explore our ${lastSelectedOption} solutions, please share your contact details so our team can provide you with a personalized demo and pricing.`,
            isBot: true,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, botResponse]);
          
          setTimeout(() => {
            // Save chat history to localStorage before redirecting
            const allMessages = [...messages, userMessage, botResponse];
            saveChatHistory(allMessages, userType, lastSelectedOption);
            
            // Open the URL in a new tab
            window.open(url, '_blank');
            
            setFormMessage("Want us to reach out with a quick walkthrough or demo? Share your details below 👇");
            setShowForm(true);
            setFormFields(['name', 'email', 'phone', 'organization', 'department', 'city', 'comments']);
          }, 1000);
        }
      } else if (cleanedOption === '⏭️ Not now') {
        const endMessage = {
          user: 'Bot',
          message: "Thank you for your time! If you'd like to explore our solutions later, just click the chat button. Have a great day! 👋",
          isBot: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, endMessage]);
        
        setTimeout(() => {
          window.parent.postMessage({ type: 'CLOSE_CHAT' }, '*');
        }, 3000);
      } else {
        // Store the selected option for later use
        const processedOption = cleanedOption
          .replace('🔍 ', '')
          .replace('🏷️ ', '')
          .replace('🤝 ', '')
          .replace('🔁 ', '')
          .replace('📲 ', '')
          .replace('📢 ', '')
          .replace('🛠️ ', '')
          .replace('🧭 ', '')
          .replace('🖥️ ', '')
          .replace('🏢 ', '')
          .replace('🧠 ', '')
          .replace('⚙️ ', '')
          .split(' – ')[0] // Get the text before the dash
          .replace(' (Totality)', ''); // Remove (Totality) from CRM Software option
        
        setLastSelectedOption(processedOption);

        let responseOptions;
        if (processedOption.includes('Pre-Sales')) {
          responseOptions = PRESALES_OPTIONS;
        } else if (processedOption === 'Sales') {
          responseOptions = SALES_OPTIONS;
        } else if (processedOption === 'Sales – Closing' || cleanedOption.includes('Sales – Closing')) {
          responseOptions = POSTSALES_OPTIONS;
        } else if (processedOption.includes('Post Sales')) {
          responseOptions = POSTSALES_OPTIONS;
        } else if (processedOption.includes('Referral Sales')) {
          responseOptions = REFERRAL_OPTIONS;
        } else if (processedOption.includes('Channel Partner Sales')) {
          responseOptions = CHANNEL_PARTNER_OPTIONS;
        } else if (processedOption.includes('Marketing')) {
          responseOptions = MARKETING_OPTIONS;
        } else if (processedOption.includes('Post Possession')) {
          responseOptions = POST_POSSESSION_OPTIONS;
        } else if (processedOption.includes('Physical Site Visits')) {
          responseOptions = PHYSICAL_SITE_VISITS_OPTIONS;
        } else if (processedOption.includes('Virtual Site Visits')) {
          responseOptions = VIRTUAL_SITE_VISITS_OPTIONS;
        } else if (processedOption.includes('Commercial Property Mgmt')) {
          responseOptions = COMMERCIAL_PROPERTY_MANAGEMENT_OPTIONS;
        } else if (processedOption.includes('CRM Software')) {
          responseOptions = CRM_SOFTWARE_OPTIONS;
        } else if (processedOption.includes('Salesforce')) {
          responseOptions = SALESFORCE_OPTIONS;
        }

        if (responseOptions) {
          const botResponse = {
            user: 'Bot',
            message: responseOptions.message,
            isBot: true,
            timestamp: new Date(),
            options: responseOptions.options
          };
          setTimeout(() => {
            setMessages(prev => [...prev, botResponse]);
          }, 500);
        } else {
          const botResponse = {
            user: 'Bot',
            message: `Great choice! Let me tell you about our ${processedOption} solutions. What specific challenges are you facing?`,
            isBot: true,
            timestamp: new Date()
          };
          setTimeout(() => {
            setMessages(prev => [...prev, botResponse]);
          }, 500);
        }
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    const loadingMessage = {
      user: 'Bot',
      message: "Submitting your information...",
      isBot: true,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, loadingMessage]);
    
    try {
      // Validate required fields
      const requiredFields = ['name', 'phone', 'email'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      }

      // Clean and format the data
      const cleanedFormData = {
        name: formData.name?.trim(),
        phone: formData.phone?.trim().replace(/[^\d+]/g, ''), // Keep only digits and + sign
        email: formData.email?.trim().toLowerCase(),
        organization: formData.organization?.trim() || '',
        department: formData.department?.trim() || '',
        city: formData.city?.trim() || '',
        comments: formData.comments?.trim() || '',
        userType: userType || 'Unknown',
        selectedOption: lastSelectedOption || 'Unknown',
        source: window.location.href,
        timestamp: new Date().toISOString()
      };

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cleanedFormData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Basic phone validation (at least 10 digits)
      const phoneDigits = cleanedFormData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        throw new Error('Please enter a valid phone number with at least 10 digits');
      }

      const response = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include',
        body: JSON.stringify(cleanedFormData),
      });

      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        // eslint-disable-next-line no-unused-vars
        const text = await response.text();
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Error saving lead');
      }


      const successMessage = {
        user: 'Bot',
        message: "Thanks! Our team will get in touch with you shortly. Meanwhile, would you like to explore other solutions?",
        isBot: true,
        timestamp: new Date(),
        options: WORKFLOW_MESSAGES['Real Estate Developer'].options
      };
      setMessages(prev => [...prev, successMessage]);
      setShowForm(false);

    } catch (error) {
      // Create a user-friendly error message
      const errorMessage = {
        user: 'Bot',
        message: error.message === 'Failed to fetch' 
          ? "Sorry, we couldn't connect to our servers. Please check your internet connection and try again."
          : `Sorry, there was an error: ${error.message}. Please try again or contact our support team.`,
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSendMessage = async (messageText) => {
    // Add user message to chat
    const userMessage = {
      user: 'User',
      message: messageText,
      isBot: false,
      timestamp: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    // Show typing indicator
    setIsTyping(true);
    
    // Track if message was handled to prevent duplicates
    let messageHandled = false;

    try {
      const requestBody = {
        sessionId: sessionIdRef.current,
        chatInput: messageText,
        action: 'chat'
      };

      // Call n8n webhook
      try {
        const response = await fetch(N8N_WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Origin': window.location.origin
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Webhook error:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });

          // Use socket.io as fallback
          if (!messageHandled) {
            socket.emit('message', userMessage);
            messageHandled = true;
          }
          return; // Exit after fallback to avoid showing error message
        }

        const rawResponse = await response.text();

        let data;
        try {
          data = JSON.parse(rawResponse);
        } catch (error) {
          throw new Error('Invalid JSON response from webhook');
        }

        // Handle different response formats
        let botMessage = '';
        if (typeof data === 'string') {
          // If response is direct string
          botMessage = data;
        } else if (typeof data === 'object') {
          // Try to find the message in various common response formats
          botMessage = data.response || data.message || data.reply || data.text || data.content || data.answer || data.output;
          
          if (!botMessage && data.data) {
            // Check if message is nested in data property
            const nestedData = data.data;
            botMessage = nestedData.response || nestedData.message || nestedData.reply || nestedData.text || nestedData.content || nestedData.output;
          }
        }

        if (!botMessage) {
          // If we couldn't find a standard message field, use the raw response
          botMessage = JSON.stringify(data);
        }

        // Check if the message contains an HTML form
        if (botMessage.includes('<form>') && botMessage.includes('</form>')) {
          // Extract the text before the form (if any) and clean it thoroughly
          let formMessage = botMessage.split('<form>')[0];
          
          // Remove markdown code block syntax
          formMessage = formMessage.replace(/```html|```/g, '');
          
          // Remove all HTML tags
          formMessage = formMessage.replace(/<\/?[^>]+(>|$)/g, '');
          
          // Remove any backticks and extra quotes that might remain
          formMessage = formMessage.replace(/[`'"]/g, '');
          
          // Clean up extra whitespace
          formMessage = formMessage.trim().replace(/\s+/g, ' ');
          
          // Show the form instead of adding as a message
          setShowForm(true);
          setFormMessage(formMessage || "Please share your details below:");
          setFormFields(['name', 'email', 'phone', 'organization', 'department', 'city', 'comments']);
          
          // Don't add a separate message to avoid duplication
          // The form will show with the extracted message
        } else {
          // Add normal bot response to chat
          const botResponse = {
            user: 'Bot',
            message: botMessage,
            isBot: true,
            timestamp: new Date(),
            options: data.options || (data.data && data.data.options) || []
          };
          setMessages(prevMessages => [...prevMessages, botResponse]);
        }
        
        // Mark as handled so we don't send to socket
        messageHandled = true;
        
        // Successfully got webhook response, no need to emit to socket
        return;

      } catch (error) {
        console.error('Error processing webhook response:', error);
        
        // Only emit to socket if webhook failed and not already handled
        if (!messageHandled) {
          socket.emit('message', userMessage);
          messageHandled = true;
        }
      } finally {
        setIsTyping(false);
      }

    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      
      // Only emit to socket if not already handled
      if (!messageHandled) {
        socket.emit('message', userMessage);
        messageHandled = true;
      }
    } finally {
      setIsTyping(false);
    }
  };

  // Add event listener for messages from parent window
  useEffect(() => {
    const handleParentMessage = (event) => {
      if (event.data && event.data.type === 'TOGGLE_CHAT') {
        setIsChatOpen(prev => !prev);
      } else if (event.data && event.data.type === 'SAVE_CHAT_HISTORY') {
        // Save chat history when requested
        saveChatHistory(messages, userType, lastSelectedOption);
      } else if (event.data && event.data.type === 'RESET_CHAT') {
        // Reset chat to initial state when requested
        isResettingRef.current = true; // Set flag to prevent saving during reset
        setMessages([INITIAL_MESSAGE]);
        setUserType(null);
        setLastSelectedOption(null);
        setShowForm(false);
        setFormFields([]);
        localStorage.removeItem('acx_chat_history');
        
        // Reset the flag after a short delay (after the page would have reloaded)
        setTimeout(() => {
          isResettingRef.current = false;
        }, 1000);
      }
    };
    
    window.addEventListener('message', handleParentMessage);
    
    // Expose toggle function to window for external control
    window.toggleAcxChat = () => {
      setIsChatOpen(prev => !prev);
    };
    
    // Expose save function to window for external access
    window.saveAcxChatHistory = () => {
      saveChatHistory(messages, userType, lastSelectedOption);
      return true; // Return success
    };
    
    // Expose reset function to window for external access
    window.resetAcxChat = () => {
      isResettingRef.current = true; // Set flag to prevent saving during reset
      setMessages([INITIAL_MESSAGE]);
      setUserType(null);
      setLastSelectedOption(null);
      setShowForm(false);
      setFormFields([]);
      localStorage.removeItem('acx_chat_history');
      
      // Reset the flag after a short delay (after the page would have reloaded)
      setTimeout(() => {
        isResettingRef.current = false;
      }, 1000);
      
      return true; // Return success
    };
    
    return () => {
      window.removeEventListener('message', handleParentMessage);
      // Clean up the exposed functions
      delete window.toggleAcxChat;
      delete window.saveAcxChatHistory;
      delete window.resetAcxChat;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, userType, lastSelectedOption]); // Add dependencies for the functions

  // Save chat history before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only save if we have messages beyond the initial message and we're not resetting
      if (messages.length > 1 && !isResettingRef.current) {
        saveChatHistory(messages, userType, lastSelectedOption);
      } else if (isResettingRef.current) {
        console.log('Skipping save during reset');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, userType, lastSelectedOption]);

  // Update return statement to include isChatOpen check
  if (!isChatOpen) {
    return null;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        p: 2,
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {messages.map((msg, index) => (
          <Message 
            key={index} 
            message={msg} 
            isBot={msg.isBot} 
            onOptionSelect={handleOptionSelect}
          />
        ))}
        {isTyping && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 2,
            }}
          >
            <CircularProgress size={16} />
            <span style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }}>
              Typing...
            </span>
          </Box>
        )}
        {showForm && (
          <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2, my: 2 }}>
            <ContactForm 
              fields={formFields} 
              onSubmit={handleFormSubmit} 
              message={formMessage}
            />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <MessageInput onSendMessage={handleSendMessage} />
    </Box>
  );
};

export default Chat; 